import { defineCustomElement } from 'vue'
import { LiveSwitchConcierge } from '@/scripts/LiveSwitchConcierge'
import styles from '@/styles/public.css?inline'
import icons from '@/styles/icons.css?inline'
import 'unfonts.css'
import { CustomElementTags } from './utility/Constants'
import { SentryFactory } from './utility/Sentry'
import { type Breadcrumb, type EventHint } from '@sentry/vue'
import { sentryTag } from './utility/Constants'

// Components
import BellPopupCe from '@/components/BellPopup.ce.vue'
import BellButtonCe from '@/components/BellButton.ce.vue'
import WindowPopupCe from '@/components/WindowPopup.ce.vue'
import DailingInCe from '@/components/DailingIn.ce.vue'
import WindowDecorationCe from '@/components/WindowDecoration.ce.vue'
import InCallCe from '@/components/InCall.ce.vue'
import EndCallCe from '@/components/EndCall.ce.vue'
import PermissionCheckCe from '@/components/PermissionCheck.ce.vue'
import KioskHomeCe from '@/components/KioskHome.ce.vue'
import FormCe from '@/components/InputForm.ce.vue'
import { MeetingSettings } from '@meeting/types/MeetingSettings'
import { useMeetingConfiguration } from '@meeting/composables/useMeetingConfiguration'
import { ZoomMeeting } from '@meeting/meeting/zoom'

import { reactive } from 'vue'

// Section below is to Polyfill for older browsers such as Safari iOS 15.3
import at from "array.prototype.at"
import { Buffer } from 'buffer'
if (!(Array as any).prototype.at) {
	(Array as any).prototype.at = at;
}
if (typeof window !== 'undefined' && !(window as any).Buffer) {
  (window as any).Buffer = Buffer
}
if (typeof window !== 'undefined') {
  const crypto = window.crypto || (window as any).msCrypto // For older IE11 support
  if (!(crypto as any).randomFillSync) {
    (crypto as any).randomFillSync = function (buffer: Uint8Array) {
      return crypto.getRandomValues(buffer)
    }
  }
}
if (typeof crypto.randomUUID !== 'function') {
  (crypto as any).randomUUID = function () {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)

    // Set version (4) and variant bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    return [...bytes]
      .map((b, i) =>
        [4, 6, 8, 10].includes(i)
          ? `-${b.toString(16).padStart(2, '0')}`
          : b.toString(16).padStart(2, '0')
      )
      .join('')
  }
}
// End of Polyfills

const BellButton = defineCustomElement(BellButtonCe)
const BellPopup = defineCustomElement(BellPopupCe)
const WindowPopup = defineCustomElement(WindowPopupCe)
const DailingIn = defineCustomElement(DailingInCe)
const WindowDecoration = defineCustomElement(WindowDecorationCe)
const InCall = defineCustomElement(InCallCe)
const EndCall = defineCustomElement(EndCallCe)
const PermissionCheck = defineCustomElement(PermissionCheckCe)
const KioskHome = defineCustomElement(KioskHomeCe)
const Form = defineCustomElement(FormCe)

customElements.get(CustomElementTags.BELL) ||
  customElements.define(CustomElementTags.BELL, BellButton)
customElements.get(CustomElementTags.POPUP) ||
  customElements.define(CustomElementTags.POPUP, BellPopup)
customElements.get(CustomElementTags.WINDOW) ||
  customElements.define(CustomElementTags.WINDOW, WindowPopup)
customElements.get(CustomElementTags.DIALING_IN) ||
  customElements.define(CustomElementTags.DIALING_IN, DailingIn)
customElements.get(CustomElementTags.WINDOW_DECORATION) ||
  customElements.define(CustomElementTags.WINDOW_DECORATION, WindowDecoration)
customElements.get(CustomElementTags.IN_CALL) ||
  customElements.define(CustomElementTags.IN_CALL, InCall)
customElements.get(CustomElementTags.END_CALL) ||
  customElements.define(CustomElementTags.END_CALL, EndCall)
customElements.get(CustomElementTags.PERMISSIONS_CHECK) ||
  customElements.define(CustomElementTags.PERMISSIONS_CHECK, PermissionCheck)
customElements.get(CustomElementTags.KIOSK_HOME) ||
  customElements.define(CustomElementTags.KIOSK_HOME, KioskHome)
customElements.get(CustomElementTags.FORM) || customElements.define(CustomElementTags.FORM, Form)

const loadLiveSwitchStylesheets = () => {
  const url = import.meta.env.VITE_URL
  const inter = document.createElement('link')
  inter.rel = 'stylesheet'
  inter.href = 'fonts/inter.css'
  document.head.appendChild(inter)
  const iconsSheet = document.createElement('style')
  iconsSheet.innerHTML = icons
  document.head.appendChild(iconsSheet)
  const stylesSheet = document.createElement('style')
  stylesSheet.innerHTML = styles
  document.head.appendChild(stylesSheet)
  const fonts = document.createElement('link')
  fonts.rel = 'stylesheet'
  fonts.href = `${url}/bubble.css`
  document.head.appendChild(fonts)
}

const globalScope = globalThis as any
export interface SentryScope {
  captureException: (e: unknown, hint?: EventHint) => void
  addBreadcrumb: (breadcrumb: Breadcrumb) => void
  setContext: (key: string, value: any) => void
  setTag: (key: string, value: any) => void
}
export const addBreadcrumb = (breadcrumb: any) => {
  if (globalScope.LSSentryScope && typeof globalScope.LSSentryScope.addBreadcrumb === 'function') {
    globalScope.LSSentryScope.addBreadcrumb(breadcrumb)
  }
}
export const setContext = (key: string, value: any) => {
  if (globalScope.LSSentryScope && typeof globalScope.LSSentryScope.setContext === 'function') {
    globalScope.LSSentryScope.setContext(key, value)
  }
}
export const setTag = (key: string, value: any) => {
  if (globalScope.LSSentryScope && typeof globalScope.LSSentryScope.setTag === 'function') {
    globalScope.LSSentryScope.setTag(key, value)
  }
}
export const captureException = (e: unknown, hint?: EventHint) => {
  if (
    globalScope.LSSentryScope &&
    typeof globalScope.LSSentryScope.captureException === 'function'
  ) {
    // Create a custom hint if not provided
    const customHint: EventHint = hint || {}

    // Ensure captureContext exists
    if (!customHint.captureContext) {
      customHint.captureContext = {}
    }

    // Add the custom tag
    customHint.captureContext = {
      ...customHint.captureContext,
      tags: {
        //@ts-expect-error Tags does exist, not sure why it doesn't find it. Remove in the future.
        ...(customHint.captureContext.tags || {}),
        source: sentryTag
      }
    }

    // Capture the exception with the custom hint
    globalScope.LSSentryScope.captureException(e, customHint)
  }
}
let retries = 0
// Initialization function
function initLiveSwitchConcierge() {
  const init = () => {
    console.log('LiveSwitch Concierge is initializing...')
    const liveswitchConcierge = new LiveSwitchConcierge()
    globalScope.liveswitchConcierge = liveswitchConcierge
    globalScope.liveSwitchConcierge = liveswitchConcierge
    globalScope.LSSentryScope = SentryFactory.createSentry().init() as SentryScope

    ZoomMeeting.assetProvider = 'CDN'
    ZoomMeeting.preload()

    // configure the meeting client
    const meetingConfiguration = useMeetingConfiguration();

    // create a new meeting settings object
    const meetingSettings = reactive(new MeetingSettings())
    meetingConfiguration.setSettings(meetingSettings);
    meetingSettings.pingUrl = `${import.meta.env.VITE_API_URL}/public/version`
    
    addBreadcrumb({
      category: 'init',
      message: 'Started Init Processing.',
      level: 'info'
    })
    loadLiveSwitchStylesheets()
    const fn = (globalThis as any).liveswitchFn
    if (fn) {
      if (Array.isArray(fn)) {
        fn.forEach((method: (scope: LiveSwitchConcierge) => void) => {
          method.call(globalThis, liveswitchConcierge)
        })
        addBreadcrumb({
          category: 'init',
          message: 'Found and run LiveSwitch FN.',
          level: 'info'
        })
      }
      ;(globalThis as any).liveswitchFn = {
        push: (method: (scope: LiveSwitchConcierge) => void) => {
          method.call(globalThis, liveswitchConcierge)
          addBreadcrumb({
            category: 'init',
            message: 'Secondary LiveSwitch FN called.',
            level: 'info'
          })
        }
      }
    } else {
      ;(globalThis as any).liveswitchFn = {
        push: (method: (scope: LiveSwitchConcierge) => void) => {
          method.call(globalThis, liveswitchConcierge)
          addBreadcrumb({
            category: 'init',
            message: 'Secondary LiveSwitch FN called.',
            level: 'info'
          })
        }
      }
      try {
        if ((globalThis as any).liveSwitchConciergeCodeName) {
          liveswitchConcierge.loadBubble((globalThis as any).liveSwitchConciergeCodeName)
          addBreadcrumb({
            category: 'init',
            message: 'Ran load bubble method.',
            level: 'info'
          })
        }
      } catch (e) {
        captureException(e)
      }
    }

    const sb = (globalThis as any).sb
    if (sb && Array.isArray(sb.q) && sb.q.length === 1) {
      const n = sb.q[0]
      if (n[0] === 'init') {
        const args: LegacyInputArgs = n[1] as LegacyInputArgs
        liveswitchConcierge.loadBubble(args.id)
        addBreadcrumb({
          category: 'init',
          message: 'Called load bubble from old script.',
          level: 'info'
        })
      }
    }

    ;(globalThis as any).sb = (eventName: string, args: LegacyInputArgs) => {
      try {
        if (eventName !== 'init' || !args || !args.id) return
        liveswitchConcierge.loadBubble(args.id)
        addBreadcrumb({
          category: 'init',
          message: 'Called load bubble from old script using function.',
          level: 'info'
        })
      } catch (e) {
        captureException(e)
      }
    }
  }
  try {
    init()
  } catch (e) {
    console.error('Error during LiveSwitch Concierge initialization:', e)
    retries++
    if (retries < 10) {
      setTimeout(initLiveSwitchConcierge, 100) // Retry after 100ms
    } else {
      captureException(e)
    }
  }
}

// Ensure the init function is called on document load or immediately if the script is added after the document is loaded
try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLiveSwitchConcierge)
  } else {
    initLiveSwitchConcierge()
  }
} catch (e) {
  captureException(e)
}

/* Legacy script tag section. Don't break. */
interface LegacyInputArgs {
  id: string
}



export {
  BellButton,
  BellPopup,
  WindowPopup,
  DailingIn,
  WindowDecoration,
  InCall,
  EndCall,
  PermissionCheck,
  KioskHome,
  Form
}
